import { api } from "../../api/api";
import { store } from "../../redux/storeConfig/store";
import {
  buildMemberCreatePayload,
  validateMemberCreatePayload,
} from "../member/createPayload";
import { getCreateCommand } from "./contracts/createCommands";
import { problemsFromMap, problemsFromReason, problemsText } from "./draftProblems";
import { persistStashedSaleIdentity } from "./voiceSaleIdentity";
import { errorText, isSuccessfulCreate } from "./confirmResponse";

const panelPath = (gymPath, trainerPath) => {
  const login = JSON.parse(
    localStorage.getItem("connectToFitGymPanelUser") || "{}"
  );
  return login.role === "TRAINER" ? trainerPath : gymPath;
};

const memberProfilePath = (memberId) => {
  if (!memberId) return "";
  return panelPath(
    `/gym-panel/membersinfo/${memberId}`,
    `/trainer-panel/membersinfo/${memberId}`
  );
};

const memberOpen = (memberId) =>
  memberId
    ? {
        openLabel: "Open member",
        openPath: memberProfilePath(memberId),
      }
    : {};

const created = (res) =>
  isSuccessfulCreate(res) && Number(res && res.status) === 201;

const emptyScanValue = (value) =>
  value === undefined || value === null || String(value).trim() === "";

const stillNeedsScan = (draft) => {
  const fields = draft.fields || {};
  return (draft.scans || []).some((scan) => {
    const key = scan && scan.key;
    return (
      emptyScanValue(key && fields[key]) &&
      emptyScanValue(fields.RFIDCardIdField) &&
      emptyScanValue(fields.cardMembershipId)
    );
  });
};

export async function confirmDraft(draft) {
  if (stillNeedsScan(draft)) return { outcome: "needs_scan", draft };
  if (draft.type === "member") return confirmMember(draft);
  return confirmByContract(draft);
}

const createContext = () => {
  const login = JSON.parse(
    localStorage.getItem("connectToFitGymPanelUser") || "{}"
  );
  const state = store.getState();
  const gymState = state.gym || {};
  const packageState = state.packages || {};
  const trainerState = state.trainer || {};
  return {
    country: login.country,
    gym: login.gym,
    gymType:
      (gymState.gym && gymState.gym.type) || login.gymType || login.type,
    gymConfig: gymState.gymConfig || {},
    timezone:
      (gymState.gym &&
        (gymState.gym.timezone || gymState.gym.timeZone)) ||
      login.timezone,
    categories: gymState.categories || [],
    packages:
      packageState.packages ||
      packageState.packageData ||
      packageState.packagesData ||
      [],
    trainers:
      trainerState.trainers ||
      trainerState.trainersData ||
      trainerState.trainerData ||
      [],
    gymData: gymState.gym || {},
    features: (state.features && state.features.features) || {},
  };
};

const idOf = (value) =>
  value && typeof value === "object" ? value.id || value.value : value;

const responseRows = (res) =>
  (res && res.status >= 200 && res.status < 300 && res.data && res.data.data) ||
  [];

const mergeLookupRows = (selected, records, defaults) =>
  (Array.isArray(selected) ? selected : selected ? [selected] : []).map(
    (item) => {
      const selection =
        item && item.value && typeof item.value === "object"
          ? item.value
          : item || {};
      const source = records.find(
        (record) => String(record.id) === String(idOf(selection))
      );
      return {
        ...(source || {}),
        ...selection,
        ...defaults(source || {}, selection),
      };
    }
  );

async function hydrateCreateInput(draft, baseContext) {
  if (draft.type === "appointment") {
    const fields = { ...(draft.fields || {}) };
    const packagesRes =
      fields.packages && fields.packages.length
        ? await api("package?page=1&limit=999999", {}, "get").catch(() => null)
        : null;
    const packageRecords = responseRows(packagesRes);
    fields.packages = mergeLookupRows(
      fields.packages,
      packageRecords,
      (source, item) => ({
        count:
          item.count ||
          parseInt(item.sessionCount || source.sessionCount, 10),
      })
    );
    return { fields, context: baseContext };
  }
  if (draft.type === "class" || draft.type === "workshop") {
    const fields = { ...(draft.fields || {}) };
    const trainerId = idOf(fields.trainer);
    const [trainerRes, packagesRes] = await Promise.all([
      trainerId
        ? api(
            `trainer/${trainerId}?dashboard=false&scheduleFilter=false`,
            {},
            "get"
          ).catch(() => null)
        : Promise.resolve(null),
      fields.assignedPackages && fields.assignedPackages.length
        ? api("package?page=1&limit=999999", {}, "get").catch(() => null)
        : Promise.resolve(null),
    ]);
    const trainer = responseRows(trainerRes);
    return {
      fields,
      context: {
        ...baseContext,
        trainers:
          trainer && !Array.isArray(trainer)
            ? [trainer]
            : baseContext.trainers,
        packages:
          responseRows(packagesRes).length > 0
            ? responseRows(packagesRes)
            : baseContext.packages,
      },
    };
  }
  if (draft.type !== "sale") {
    return { fields: draft.fields || {}, context: baseContext };
  }
  const fields = { ...(draft.fields || {}) };
  const requests = [];
  const memberId = idOf(fields.member);
  requests.push(
    memberId
      ? api(`member/${memberId}`, {}, "get").catch(() => null)
      : Promise.resolve(null)
  );
  requests.push(
    fields.packages && fields.packages.length
      ? api("package?page=1&limit=999999", {}, "get").catch(() => null)
      : Promise.resolve(null)
  );
  requests.push(
    fields.products && fields.products.length
      ? api("products?page=1&limit=999999", {}, "get").catch(() => null)
      : Promise.resolve(null)
  );
  const [memberRes, packagesRes, productsRes] = await Promise.all(requests);
  const member = responseRows(memberRes);
  const resolvedMember =
    member && !Array.isArray(member) ? member : fields.member;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  fields.member = resolvedMember;
  fields.packages = mergeLookupRows(
    fields.packages,
    responseRows(packagesRes),
    (source, item) => ({
      member: idOf(item.member) || memberId,
      price: item.price !== undefined ? item.price : source.price,
      duration:
        item.duration !== undefined ? item.duration : source.durationCount,
      startDate: item.startDate || today.getTime(),
      discount: item.discount || 0,
    })
  );
  fields.products = mergeLookupRows(
    fields.products,
    responseRows(productsRes),
    (source, item) => ({
      member: idOf(item.member) || memberId,
      price: item.price !== undefined ? item.price : source.price,
      quantity: item.quantity || 1,
      discount: item.discount || 0,
    })
  );
  return {
    fields,
    context: {
      ...baseContext,
      member: resolvedMember,
      gym: baseContext.gymData || {},
      gymId: idOf(baseContext.gym) || baseContext.gym,
      gymFeaturesObject: baseContext.features,
    },
  };
}

const saleMemberId = (input) => {
  const fields = (input && input.fields) || {};
  const member = fields.member || (input && input.context && input.context.member);
  if (member && typeof member === "object") {
    return member.id || (member.value && member.value.id) || member.value;
  }
  return fields.user && (fields.user.value || fields.user.id);
};

async function uploadSaleIdentityIfNeeded(draft, input) {
  if (!draft || draft.type !== "sale") return { ok: true };
  const memberId = saleMemberId(input);
  const uploaded = await persistStashedSaleIdentity(draft, memberId);
  if (uploaded.skipped) return { ok: true };
  if (uploaded.ok) return { ok: true };
  return {
    ok: false,
    message:
      uploaded.message || "Upload the member identity document in the sale form.",
  };
}

async function confirmByContract(draft) {
  const command = getCreateCommand(draft.type);
  if (!command) {
    return {
      outcome: "needs_form",
      draft,
      message: "This add type is not available for batch creation.",
    };
  }
  const input = await hydrateCreateInput(draft, createContext());
  const identity = await uploadSaleIdentityIfNeeded(draft, input);
  if (!identity.ok) {
    const problems = problemsFromReason({
      code: "identity_document_required",
      message: identity.message,
      fields: ["attachment"],
    });
    return {
      outcome: "needs_form",
      draft: {
        ...draft,
        fields: { ...(draft.fields || {}), requiredAttachment: true },
        fieldErrors: problems,
      },
      problems,
      message: identity.message,
    };
  }
  const prepared = command.preparePayload(input.fields, input.context);
  if (prepared.outcome !== "ready") {
    const problems = problemsFromReason(prepared.reason);
    const needsIdentity =
      prepared.reason && prepared.reason.code === "identity_document_required";
    return {
      outcome: "needs_form",
      draft: {
        ...draft,
        fields: needsIdentity
          ? { ...(draft.fields || {}), requiredAttachment: true }
          : draft.fields,
        fieldErrors: problems,
      },
      problems,
      message:
        problemsText(
          problems,
          (prepared.reason && prepared.reason.message) ||
            "Open the form to fix the invalid or missing details."
        ),
    };
  }
  try {
    const res = await api(
      prepared.endpoint,
      prepared.payload,
      prepared.method || "post"
    );
    if (!isSuccessfulCreate(res)) {
      return { outcome: "error", draft, message: errorText(res) };
    }
    const record = res.data && res.data.data;
    if (draft.type === "package" && record && record.id && !record.isActive) {
      try {
        await api(`package/${record.id}/toggleActivation`, {}, "patch");
      } catch (error) {
        /* Keep the created package even if activation fails. */
      }
    }
    return {
      outcome: "created",
      draft,
      createdId: record && record.id,
      createdName:
        (record && (record.name || record.title)) ||
        (draft.fields && (draft.fields.name || draft.fields.title)),
    };
  } catch (error) {
    return {
      outcome: "error",
      draft,
      message: `Could not create ${String(draft.label || "this record").toLowerCase()}.`,
    };
  }
}

async function confirmMember(draft) {
  const fields = draft.fields || {};
  const name = [fields.firstName, fields.lastName].filter(Boolean).join(" ");
  const existingId = draft.existingMemberId || fields.id;
  if (existingId && String(existingId).indexOf("member-") !== 0) {
    return {
      outcome: "used",
      draft,
      createdId: existingId,
      createdName: name || fields.name,
      message: "Using the selected member.",
      ...memberOpen(existingId),
    };
  }
  const payload = buildMemberCreatePayload({
    ...fields,
    name,
    relations: fields.relations || [],
  });
  const validationErrors = validateMemberCreatePayload(payload);
  if (!name) {
    validationErrors.name = "First name and last name are required";
  }
  if (Object.keys(validationErrors).length || draft.needsForm) {
    const problems = problemsFromMap(validationErrors);
    if (!problems.length) {
      problems.push({
        field: "",
        label: "",
        message: "Open the form to complete the missing member details.",
      });
    }
    return {
      outcome: "needs_form",
      draft: { ...draft, fieldErrors: problems },
      problems,
      message: problemsText(
        problems,
        "Open the form to fix the invalid or missing details."
      ),
    };
  }

  try {
    const res = await api("member", payload, "post");
    if (res && res.status === 200) {
      const existingMember = res.data && res.data.data;
      const existingId = existingMember && existingMember.id;
      return {
        outcome: "existing",
        draft,
        createdId: existingId,
        createdName: name,
        message:
          "This member already exists. Edit the details, or select a member to continue.",
        ...memberOpen(existingId),
      };
    }
    if (!created(res)) {
      return { outcome: "error", draft, message: errorText(res) };
    }
    const createdMember = res.data && res.data.data;
    const createdId = createdMember && createdMember.id;
    return {
      outcome: "created",
      draft,
      createdId,
      createdName: name,
      ...memberOpen(createdId),
    };
  } catch (error) {
    return { outcome: "error", draft, message: "Could not create this member." };
  }
}
